import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { Fingerprint, Lock, Loader2, LogOut, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { audioAlerts } from '../../utils/audioAlerts';
import { isPasskeyAvailableOnThisDevice } from '../../auth/passkeyDevice';
import {
  requestLoginPasskeyOptions,
  verifyLoginPasskey,
} from '../../security/passkey/passkeyApi';
import { BiometricGateView } from './BiometricGateView';

interface PwaBiometricLockContextType {
  locked: boolean;
  unlock: () => void;
}

const PwaBiometricLockContext = createContext<PwaBiometricLockContextType | undefined>(undefined);

export function usePwaBiometricLock() {
  const context = useContext(PwaBiometricLockContext);
  if (!context) {
    throw new Error('usePwaBiometricLock must be used within a PwaBiometricLockProvider');
  }
  return context;
}

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
}

function hasMobileBiometric(userLike: { biometriaAtiva?: boolean; passKeyDevices?: string[] } | null | undefined): boolean {
  return userLike?.biometriaAtiva === true || (userLike?.passKeyDevices || []).includes('mobile');
}

type PasskeyOptionsEnvelope = Record<string, any> & {
  options?: Record<string, any>;
};

function unwrapPasskeyOptions(optionsData: PasskeyOptionsEnvelope): Record<string, any> {
  return optionsData.options || optionsData;
}

interface Props {
  children: React.ReactNode;
}

export function PwaBiometricLockProvider({ children }: Props) {
  const { isAuthenticated, user, login, logout } = useAuthStore();
  const [locked, setLocked] = useState(() => {
    if (typeof window !== 'undefined') {
      if (!isMobileDevice()) {
        return false;
      }
      const jaDesbloqueouSessao = sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';
      if (jaDesbloqueouSessao) {
        return false;
      }
      // Se não desbloqueou ainda na sessão atual, checa se tem login ativo com biometria no celular
      const tokenExiste = localStorage.getItem('ac_token');
      const userSalvo = localStorage.getItem('ac_user');
      let biometriaAtivaLocal = false;
      if (userSalvo) {
        try {
          const u = JSON.parse(userSalvo);
          biometriaAtivaLocal = hasMobileBiometric(u) && isPasskeyAvailableOnThisDevice(u.email);
        } catch {}
      }
      if (tokenExiste && biometriaAtivaLocal) {
        return true;
      }
      return localStorage.getItem('ac_pwa_locked') === 'true';
    }
    return false;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const lastBlurTime = useRef<number | null>(null);
  const isExecutingRef = useRef(false);

  const activeUserEmail = user?.email;
  const userHasBiometrics = isAuthenticated && user && hasMobileBiometric(user) && isPasskeyAvailableOnThisDevice(user.email);

  // Persistir estado de bloqueio no localStorage (para sobreviver a reload acidental)
  useEffect(() => {
    if (locked) {
      localStorage.setItem('ac_pwa_locked', 'true');
    } else {
      localStorage.removeItem('ac_pwa_locked');
    }
  }, [locked]);

  // Desbloquear e limpar qualquer trava caso o dispositivo seja desktop
  useEffect(() => {
    if (!isMobileDevice()) {
      localStorage.removeItem('ac_pwa_locked');
      setLocked(false);
    }
  }, []);

  // Se o aplicativo estiver bloqueado e o dispositivo não possuir a biometria local, força formulário de senha
  useEffect(() => {
    if (locked && !userHasBiometrics) {
      setShowPasswordForm(true);
    }
  }, [locked, userHasBiometrics]);

  // Bloquear na inicialização (Cold Start) se estiver no celular com biometria ativa local e não desbloqueou nesta sessão
  useEffect(() => {
    const jaDesbloqueouSessao = sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';
    if (isMobileDevice() && userHasBiometrics && !jaDesbloqueouSessao && !locked) {
      setLocked(true);
    }
  }, [userHasBiometrics]);

  // Função para executar a autenticação biométrica (Passkey)
  const executarBiometria = useCallback(async () => {
    if (!activeUserEmail || !userHasBiometrics || isExecutingRef.current) return;
    
    isExecutingRef.current = true;
    setLoading(true);
    setError('');

    try {
      // 1. Obter opções de desafio biométrico do servidor
      const optionsData = await requestLoginPasskeyOptions(activeUserEmail);
      const options = unwrapPasskeyOptions(optionsData as PasskeyOptionsEnvelope);
      if (!options) {
        throw new Error('Servidor não retornou opções de biometria válidas.');
      }

      // 2. Disparar diálogo biométrico nativo (fingerprint/face unlock)
      const assertion = await startAuthentication({ optionsJSON: options as any });

      // 3. Verificar assinatura no backend
      const data = await verifyLoginPasskey(activeUserEmail, assertion);
      if (!data.user || !data.access_token) {
        throw new Error('Verificação biométrica não autorizada pelo servidor.');
      }

      const userData = {
        ...(data.user as any),
        role: (data.user as any).perfil,
        roles: [(data.user as any).perfil],
      };
      
      if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
      
      // Atualiza token e dados do usuário no estado global
      login(data.access_token, userData);

      // Desbloquear app e registrar na sessão
      sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');
      localStorage.removeItem('ac_last_pwa_unlock_date'); // Limpar lógica antiga do localStorage
      setLocked(false);
      setShowPasswordForm(false);
      setError('');
      audioAlerts.playUnlock();
    } catch (err: any) {
      console.warn('[PwaBiometricLock] Erro no prompt biométrico:', err);
      
      // Se for cancelamento explícito do usuário, não mostramos erro escandaloso,
      // apenas abrimos o fallback de senha.
      if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
        setError('Validação biométrica necessária ou utilize sua senha.');
      } else {
        setError(err.message || 'Falha ao autenticar biometria.');
      }
      setShowPasswordForm(true);
    } finally {
      setLoading(false);
      isExecutingRef.current = false;
    }
  }, [activeUserEmail, userHasBiometrics, login]);

  // Função para destravar usando senha tradicional (fallback de segurança)
  const handlePasswordUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeUserEmail || password.length < 6) return;

    setLoading(true);
    setError('');
    const API = import.meta.env.VITE_API_URL || '/api/v1';

    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: activeUserEmail, senha: password }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Senha incorreta.');
      }

      const data = await res.json();
      const userData = { ...data.user, role: data.user.perfil, roles: [data.user.perfil] };
      
      if (data.refresh_token) localStorage.setItem('ac_refresh_token', data.refresh_token);
      
      login(data.access_token, userData);
      
      sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');
      localStorage.removeItem('ac_last_pwa_unlock_date'); // Limpar lógica antiga do localStorage
      setLocked(false);
      setPassword('');
      setShowPasswordForm(false);
      audioAlerts.playUnlock();
    } catch (err: any) {
      setError(err.message || 'Erro ao validar senha de acesso.');
    } finally {
      setLoading(false);
    }
  };

  // Logout completo para permitir que outro usuário logue no aparelho
  const handleLogout = () => {
    logout();
    sessionStorage.removeItem('ac_pwa_unlocked_this_session');
    localStorage.removeItem('ac_last_pwa_unlock_date');
    localStorage.removeItem('ac_pwa_locked');
    setLocked(false);
    setShowPasswordForm(false);
    setPassword('');
  };

  // Verificação silenciosa de segurança no boot (se estiver travado)
  useEffect(() => {
    if (!locked) return;

    const verificarSessaoESeguranca = async () => {
      const tokenLocal = localStorage.getItem('ac_token');
      if (!tokenLocal) {
        // Se não há token, limpa a trava e faz logout
        handleLogout();
        return;
      }

      const API = import.meta.env.VITE_API_URL || '/api/v1';
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${tokenLocal}` },
        });

        if (!res.ok) {
          // Token inválido ou expirado -> remove a trava e força logout
          console.warn('[PwaBiometricLock] Sessão expirada/inválida detectada no boot. Efetuando logout.');
          handleLogout();
          return;
        }

        const data = await res.json();
        if (data.biometriaAtiva === false) {
          // Se a biometria foi revogada/desativada no servidor, destrava o app e limpa estado local
          console.warn('[PwaBiometricLock] Biometria desativada pelo servidor. Destravando tela.');
          sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');
          localStorage.removeItem('ac_pwa_locked');
          setLocked(false);
        }
      } catch (err) {
        console.warn('[PwaBiometricLock] Erro ao validar sessão silenciosamente no boot:', err);
      }
    };

    verificarSessaoESeguranca();
  }, [locked]);

  // Monitorar retorno de segundo plano (Warm Start)
  useEffect(() => {
    if (!isMobileDevice() || !userHasBiometrics) return;

    const handleVisibilityChange = () => {
      const jaDesbloqueouSessao = sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';

      if (document.visibilityState === 'hidden') {
        lastBlurTime.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        if (lastBlurTime.current) {
          const diff = Date.now() - lastBlurTime.current;
          // Se o app ficou em background por mais de 10 segundos, bloqueamos
          // apenas se o usuário ainda não destravou na sessão atual
          if (diff > 10000 && !jaDesbloqueouSessao) {
            setLocked(true);
          }
        }
      }
    };

    const handleFocus = () => {
      // Reforço de foco caso o visibilitychange não dispare
      const jaDesbloqueouSessao = sessionStorage.getItem('ac_pwa_unlocked_this_session') === 'true';

      if (document.visibilityState === 'visible' && lastBlurTime.current) {
        const diff = Date.now() - lastBlurTime.current;
        if (diff > 10000 && !jaDesbloqueouSessao) {
          setLocked(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userHasBiometrics]);

  const isBypassedRoute = typeof window !== 'undefined' && (
    window.location.pathname === '/' ||
    window.location.pathname.startsWith('/login') ||
    window.location.pathname.startsWith('/security-compliance') ||
    window.location.pathname.startsWith('/ativar') ||
    window.location.pathname.startsWith('/install') ||
    window.location.pathname.startsWith('/security/') ||
    window.location.pathname.startsWith('/onboarding/') ||
    window.location.pathname.startsWith('/exemplos') ||
    window.location.pathname.startsWith('/download') ||
    window.location.pathname.startsWith('/tv') ||
    window.location.pathname.startsWith('/simulador') ||
    window.location.pathname.startsWith('/demo') ||
    window.location.pathname.startsWith('/ecad') ||
    window.location.pathname === '/convite'
  );

  // Auto-disparar a biometria quando travar a tela
  useEffect(() => {
    if (locked && userHasBiometrics && !showPasswordForm && !loading && !isBypassedRoute) {
      // Pequeno delay para garantir foco total e evitar concorrência de renderização
      const timer = setTimeout(() => {
        executarBiometria();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [locked, userHasBiometrics, showPasswordForm, executarBiometria, isBypassedRoute]);

  // Se não estiver bloqueado ou for rota de segurança/onboarding/instalação/ativação, renderiza os filhos normalmente
  if (!locked || isBypassedRoute) {
    return (
      <PwaBiometricLockContext.Provider value={{ locked, unlock: executarBiometria }}>
        {children}
      </PwaBiometricLockContext.Provider>
    );
  }

  // Renderiza a tela de bloqueio biométrica premium
  return (
    <PwaBiometricLockContext.Provider value={{ locked, unlock: executarBiometria }}>
      <BiometricGateView
        mode="unlock"
        title="Aplicativo bloqueado"
        subtitle="Confirme sua identidade para acessar o Aluno Conectado neste aparelho."
        account={user}
        loading={loading}
        error={error}
        primaryLabel="Usar biometria"
        primaryLoadingLabel="Aguardando biometria..."
        onPrimary={executarBiometria}
        secondaryLabel="Entrar com senha"
        onSecondary={() => setShowPasswordForm(true)}
        logoutLabel="Sair desta conta"
        onLogout={handleLogout}
      >
        {showPasswordForm ? (
          <form onSubmit={handlePasswordUnlock} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha de acesso"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                autoFocus
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={eyeBtnStyle}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || password.length < 6}
              style={submitBtnStyle}
            >
              {loading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'pwa-spin 1s linear infinite' }} />
                  Verificando...
                </>
              ) : (
                'Desbloquear acesso'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowPasswordForm(false);
                setError('');
              }}
              style={toggleFormBtnStyle}
            >
              Voltar para biometria
            </button>
          </form>
        ) : undefined}
      </BiometricGateView>
      {false && user && (
      <div style={lockOverlayStyle}>
        <div style={glassCardStyle}>
          {/* Top Shield Icon */}
          <div style={lockIconContainerStyle}>
            <Lock size={22} style={{ color: '#ea580c' }} />
          </div>

          <h2 style={titleStyle}>Aplicativo Bloqueado</h2>
          <p style={subtitleStyle}>
            Por segurança, confirme sua identidade para acessar o Aluno Conectado.
          </p>

          {/* User info */}
          {user && (
            <div style={userCardStyle}>
              {user?.fotoUrl ? (
                <img src={user?.fotoUrl || undefined} alt={user?.nome || 'Conta'} style={avatarStyle} />
              ) : (
                <div style={avatarFallbackStyle}>
                  {(user?.nome || 'AC').substring(0, 2).toUpperCase()}
                </div>
              )}
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>
                  {user?.nome?.split(' ')[0] || 'Conta'}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  {user?.email}
                </div>
              </div>
            </div>
          )}

          {error && <div style={errorStyle}>{error}</div>}

          {!showPasswordForm ? (
            <div style={{ width: '100%' }}>
              {/* Botão de digital */}
              <button
                onClick={executarBiometria}
                disabled={loading}
                style={biometriaBtnStyle}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'pwa-spin 1s linear infinite' }} />
                    Aguardando biometria...
                  </>
                ) : (
                  <>
                    <Fingerprint size={24} />
                    Usar Biometria
                  </>
                )}
              </button>

              <button
                onClick={() => setShowPasswordForm(true)}
                style={toggleFormBtnStyle}
              >
                <KeyRound size={14} /> Entrar com senha
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordUnlock} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha de acesso"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={eyeBtnStyle}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || password.length < 6}
                style={submitBtnStyle}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'pwa-spin 1s linear infinite' }} />
                    Verificando...
                  </>
                ) : (
                  'Desbloquear Acesso'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setError('');
                }}
                style={toggleFormBtnStyle}
              >
                Voltar para biometria
              </button>
            </form>
          )}

          {/* Sair da conta */}
          <button
            onClick={handleLogout}
            style={logoutBtnStyle}
          >
            <LogOut size={14} /> Sair desta conta
          </button>
        </div>

        <style>{`
          @keyframes pwa-spin { to { transform: rotate(360deg); } }
          @keyframes pwa-fade-in { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
      )}
    </PwaBiometricLockContext.Provider>
  );
}

// ── Styles ──
const lockOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 999999,
  background: 'radial-gradient(circle at center, #0f172a 0%, #020617 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  fontFamily: 'Inter, system-ui, sans-serif',
  animation: 'pwa-fade-in 0.3s ease-out',
};

const glassCardStyle: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.75)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 24,
  backdropFilter: 'blur(20px)',
  padding: '32px 24px',
  width: '100%',
  maxWidth: 380,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
};

const lockIconContainerStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'rgba(234, 88, 12, 0.1)',
  border: '1px solid rgba(234, 88, 12, 0.25)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 800,
  color: '#f8fafc',
  margin: 0,
  letterSpacing: '-0.02em',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  color: '#94a3b8',
  margin: '8px 0 20px',
  lineHeight: 1.5,
};

const userCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: 14,
  padding: '10px 14px',
  width: '100%',
  marginBottom: 20,
};

const avatarStyle: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  objectFit: 'cover',
};

const avatarFallbackStyle: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #4f46e5, #312e81)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.82rem',
  fontWeight: 800,
};

const errorStyle: React.CSSProperties = {
  color: '#f87171',
  fontSize: '0.78rem',
  fontWeight: 600,
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.15)',
  borderRadius: 10,
  padding: '8px 12px',
  width: '100%',
  marginBottom: 16,
  textAlign: 'center',
};

const biometriaBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px',
  borderRadius: 14,
  border: 'none',
  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
  color: '#ffffff',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  boxShadow: '0 4px 14px rgba(234, 88, 12, 0.3)',
  transition: 'transform 0.15s ease',
};

const submitBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px',
  borderRadius: 14,
  border: 'none',
  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 52%, #581c87 100%)',
  color: '#ffffff',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
};

const toggleFormBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#94a3b8',
  fontSize: '0.78rem',
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 14,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  textDecoration: 'none',
};

const logoutBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#64748b',
  fontSize: '0.78rem',
  fontWeight: 600,
  cursor: 'pointer',
  marginTop: 32,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 44px 14px 16px',
  borderRadius: 14,
  border: '1.5px solid rgba(255, 255, 255, 0.1)',
  background: 'rgba(0, 0, 0, 0.2)',
  color: '#ffffff',
  fontSize: '0.92rem',
  fontWeight: 500,
  outline: 'none',
  boxSizing: 'border-box',
};

const eyeBtnStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: 14,
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  color: '#64748b',
  cursor: 'pointer',
  padding: 0,
};
